import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { GeoRegion } from '../data/countries';
import ShareMetaTags from '../components/ShareMetaTags';
import CountryBadge from '../components/CountryBadge';
import QuizBreakdown from '../components/QuizBreakdown';
import useDocumentTitle from '../hooks/useDocumentTitle';
import BadgeAchievementPopup from '../components/BadgeAchievementPopup';
import ResultModal from '../components/ResultModal';
import { useAuth } from '../contexts/AuthContext';

interface ShareData {
  shareText: string;
  shareUrl: string;
  username: string;
  isPremium?: boolean;
  breakdown?: import('../components/QuizBreakdown').BreakdownQuestion[];
  geoRegion?: GeoRegion | string;
  country?: string | null;
  countryCode?: string | null;
  score: number;
  questionsCorrect: number;
  totalQuestions: number;
  completionTime: number;
  dateCompleted: string;
  quizType?: string;
  topicSlot?: number | null;
  topicName?: string | null;
  rank: number;
  totalPlayersToday?: number;
  percentile?: number;
  countryRank?: number | null;
  countryTotalPlayers?: number | null;
  isTopInCountry?: boolean;
  userStreak?: number;
  globalAverageScore?: number;
  regionAverageScore?: number | null;
  countryAverageScore?: number | null;
}

interface SocialPlatform {
  id: string;
  name: string;
  icon: string;
  url: string;
  color?: string;
}


// Helper function to calculate XP progress (matches server-side logic)
const getXPProgress = (currentLevel: number, totalXP: number): { xpToNextLevel: number; progress: number; isMaxLevel: boolean } => {
  const getXPForLevel = (level: number): number => {
    if (level <= 1) return 0;
    return Math.floor(400 * Math.pow(level, 1.5));
  };

  // Ensure level is at least 1
  const safeLevel = Math.max(1, currentLevel);
  
  const currentLevelXP = getXPForLevel(safeLevel);
  const nextLevelXP = getXPForLevel(safeLevel + 1);
  const xpInCurrentLevel = totalXP - currentLevelXP;
  const xpNeededForNextLevel = nextLevelXP - currentLevelXP;
  const xpToNextLevel = nextLevelXP - totalXP;

  // Only show "Max Level Reached" if we're at a very high level (level 100+) and have enough XP
  // For normal levels (especially level 1), always show progress
  // Level 1 users should never see "Max Level Reached"
  const isMaxLevel = safeLevel >= 100 && xpToNextLevel <= 0 && xpNeededForNextLevel <= 0;
  
  // Calculate progress (0-1)
  // For level 1, nextLevelXP should be getXPForLevel(2) which is ~1131
  // If xpNeededForNextLevel is 0 or negative for low levels, show 0% progress
  let finalProgress: number;
  if (xpNeededForNextLevel > 0) {
    finalProgress = Math.min(1, Math.max(0, xpInCurrentLevel / xpNeededForNextLevel));
  } else if (isMaxLevel) {
    finalProgress = 1; // Truly at max level
  } else {
    // This shouldn't happen for normal levels, but if it does, show 0% progress
    finalProgress = 0;
  }

  return {
    xpToNextLevel: Math.max(0, xpToNextLevel),
    progress: finalProgress,
    isMaxLevel
  };
};

// Helper function to get dynamic headline based on performance
const getPerformanceHeadline = (share: ShareData): string => {
  if (share.rank === 1) {
    return 'Todays Champion!';
  }
  if (share.percentile && share.percentile >= 95) {
    return 'Outstanding!';
  }
  if (share.percentile && share.percentile >= 90) {
    return 'Champion Performance!';
  }
  if (share.percentile && share.percentile >= 75) {
    return 'Top Performer!';
  }
  if (share.percentile && share.percentile >= 50) {
    return 'Strong Performance!';
  }
  return 'Good Effort!';
};

// Helper function to format percentile text
// percentile represents the percentage of players below this score (0-100)
const formatPercentile = (percentile?: number, totalPlayers?: number): string => {
  if (!percentile && percentile !== 0) return '';
  
  // Clamp percentile to valid range
  const clampedPercentile = Math.max(0, Math.min(100, percentile));
  
  // For very small player counts, show appropriate message
  if (totalPlayers && totalPlayers <= 1) {
    return 'Only Player';
  }
  
  // Calculate what percentage of players are above you
  
  // For top performers
  if (clampedPercentile >= 99) return 'Top 1%';
  if (clampedPercentile >= 95) return 'Top 5%';
  if (clampedPercentile >= 90) return 'Top 10%';
  if (clampedPercentile >= 75) return 'Top 25%';
  if (clampedPercentile >= 50) return 'Top 50%';
  
  // For lower percentiles, show the actual top percentage
  // If percentile is 30, then 70% are above, so you're in top 30%
  const topPercentage = Math.max(1, Math.ceil(clampedPercentile));
  return `Top ${topPercentage}%`;
};

// Calculate accuracy percentage
const calculateAccuracy = (correct: number, total: number): number => {
  return total > 0 ? Math.round((correct / total) * 100) : 0;
};

const Share: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [share, setShare] = useState<ShareData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  const [comparisonsExpanded, setComparisonsExpanded] = useState(true);
  const [shareExpanded, setShareExpanded] = useState(false);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [levelUpData, setLevelUpData] = useState<any>(null);
  const [showBadgePopup, setShowBadgePopup] = useState(false);
  const [newBadges, setNewBadges] = useState<string[]>([]);
  const [xpData, setXpData] = useState<{ xpEarned: number; currentLevel: number; totalXP: number; xpToNextLevel: number; progressToNextLevel: number } | null>(null);
  const { user } = useAuth();
  const [userCountry, setUserCountry] = useState<{ country?: string; countryCode?: string } | null>(null);
  const [averages, setAverages] = useState<{
    global: { avgScore?: number; avgAccuracy?: number; avgTime?: number };
    region: { avgScore?: number; avgAccuracy?: number; avgTime?: number };
    country: { avgScore?: number; avgAccuracy?: number; avgTime?: number };
  }>({ global: {}, region: {}, country: {} });
  useDocumentTitle('Cognidaily | Shared Result');

  const displayScore = share?.score || 0;
  const displayRank = share?.rank || 0;
  const displayAccuracy = share ? calculateAccuracy(share.questionsCorrect, share.totalQuestions) : 0;

  useEffect(() => {
    if (!token) {
      setError('Share link is missing.');
      setLoading(false);
      return;
    }

    const fetchShare = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await axios.get(`/api/quiz/share/${token}`);

        if (response.data.success) {
          setShare(response.data.data);
        } else {
          setError(response.data.message || 'We could not find that share link.');
        }
      } catch (err: any) {
        const message = err.response?.data?.error || err.response?.data?.message || err.message;
        setError(message || 'We could not find that share link.');
      } finally {
        setLoading(false);
      }
    };

    fetchShare();
  }, [token]);

  // Fetch averages data when share is loaded (all shares are competitive in 5-topic model)
  useEffect(() => {
    if (!share) return;

    const loadAverages = async () => {
      try {
        const averagesRes = await axios.get('/api/user/averages');
        if (averagesRes.data.success) {
          setAverages({
            global: {
              avgScore: share.globalAverageScore || averagesRes.data.data.global.avgScore,
              avgAccuracy: averagesRes.data.data.global.avgAccuracy,
              avgTime: averagesRes.data.data.global.avgTime
            },
            region: {
              avgScore: share.regionAverageScore || undefined,
              avgAccuracy: undefined, // Region accuracy not available in share data
              avgTime: undefined // Region time not available in share data
            },
            country: {
              avgScore: share.countryAverageScore || averagesRes.data.data.country.avgScore,
              avgAccuracy: averagesRes.data.data.country.avgAccuracy,
              avgTime: averagesRes.data.data.country.avgTime
            }
          });
        }
      } catch (err) {
        console.error('Failed to load averages:', err);
        // Fallback to using data from share
        setAverages({
          global: {
            avgScore: share.globalAverageScore
          },
          region: {
            avgScore: share.regionAverageScore || undefined
          },
          country: {
            avgScore: share.countryAverageScore || undefined
          }
        });
      }
    };

    loadAverages();
  }, [share]);

  // Check for level-up data from sessionStorage on mount
  useEffect(() => {
    const storedLevelUp = sessionStorage.getItem('quizLevelUp');
    if (storedLevelUp) {
      try {
        const levelUpData = JSON.parse(storedLevelUp);
        setLevelUpData(levelUpData);
        setShowLevelUp(true);
        
        // Also set XP data for display (use newLevel as currentLevel after level up)
        setXpData({
          xpEarned: levelUpData.xpEarned,
          currentLevel: levelUpData.newLevel,
          totalXP: levelUpData.totalXP,
          xpToNextLevel: levelUpData.xpToNextLevel,
          progressToNextLevel: levelUpData.progressToNextLevel || 0
        });
        
        // Don't clear from sessionStorage yet - keep it until after XP section renders
        // sessionStorage.removeItem('quizLevelUp');
      } catch (err) {
        console.error('Failed to parse level-up data:', err);
        sessionStorage.removeItem('quizLevelUp');
      }
    } else {
      // If no level-up, try to get XP data from sessionStorage
      // This handles cases where XP was earned but no level-up occurred
      const storedXPData = sessionStorage.getItem('quizXPData');
      if (storedXPData) {
        try {
          const xpData = JSON.parse(storedXPData);
          // Calculate progress if not provided
          let xpToNextLevel = xpData.xpToNextLevel;
          let progressToNextLevel = xpData.progressToNextLevel;
          
          if (xpToNextLevel === null || xpToNextLevel === undefined || progressToNextLevel === null || progressToNextLevel === undefined) {
            const progressData = getXPProgress(xpData.currentLevel || 1, xpData.totalXP || 0);
            xpToNextLevel = progressData.xpToNextLevel;
            progressToNextLevel = progressData.progress;
          }
          
          setXpData({
            xpEarned: xpData.xpEarned,
            currentLevel: xpData.currentLevel || 1,
            totalXP: xpData.totalXP || 0,
            xpToNextLevel: xpToNextLevel || 0,
            progressToNextLevel: progressToNextLevel || 0
          });
          // Don't clear from sessionStorage yet - keep it until after XP section renders
          // sessionStorage.removeItem('quizXPData');
        } catch (err) {
          console.error('Failed to parse XP data:', err);
          sessionStorage.removeItem('quizXPData');
        }
      }
    }
  }, []);

  // Clear sessionStorage after component has mounted and XP data is set
  useEffect(() => {
    if (xpData) {
      // Small delay to ensure XP section has rendered
      const timer = setTimeout(() => {
        sessionStorage.removeItem('quizLevelUp');
        sessionStorage.removeItem('quizXPData');
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [xpData]);

  // Fetch XP data from user profile if not in sessionStorage and user is authenticated
  useEffect(() => {
    // Only fetch if we don't have XP data and user is authenticated and share data is loaded
    if (!xpData && user && share) {
      const fetchUserXP = async () => {
        try {
          const response = await axios.get('/api/user/profile');
          if (response.data.success && response.data.data) {
            // Store user profile data for country information
            const profileUser = response.data.data.user;
            if (profileUser && (profileUser.country || profileUser.countryCode)) {
              // Store country from profile to ensure consistency
              setUserCountry({
                country: profileUser.country,
                countryCode: profileUser.countryCode
              });
            }
            
            // Get XP data from the profile response (same structure as profile page uses)
            const profileXpData = response.data.data.xp;
            if (profileXpData) {
              // For competitive quizzes, XP earned equals the score
              const xpEarned = share.score;
              
              // Use the user's CURRENT XP data from profile (before this quiz)
              // This ensures consistency with the profile page
              setXpData({
                xpEarned,
                currentLevel: profileXpData.currentLevel || 1,
                totalXP: profileXpData.totalXP || 0,
                xpToNextLevel: profileXpData.xpToNextLevel || 0,
                progressToNextLevel: profileXpData.progressToNextLevel || 0
              });
            } else {
              // Fallback if XP data not in expected format - try to get from user data
              const userData = response.data.data;
              const xpEarned = share.score;
              const currentTotalXP = userData.xp?.totalXP || userData.totalXP || userData.total_xp || 0;
              const currentLevel = userData.xp?.currentLevel || userData.currentLevel || userData.current_level || 1;
              
              // Calculate progress using current XP (not adding the earned XP)
              const progressData = getXPProgress(currentLevel, currentTotalXP);
              
              setXpData({
                xpEarned,
                currentLevel,
                totalXP: currentTotalXP,
                xpToNextLevel: progressData.xpToNextLevel,
                progressToNextLevel: progressData.progress
              });
            }
          }
        } catch (err) {
          console.error('Failed to fetch user XP data:', err);
          // Fallback: use score as XP earned with default level
          setXpData({
            xpEarned: share.score,
            currentLevel: 1,
            totalXP: share.score,
            xpToNextLevel: 0,
            progressToNextLevel: 0
          });
        }
      };
      
      fetchUserXP();
    }
  }, [user, share, xpData]);

  // Check for newly awarded badges from sessionStorage
  useEffect(() => {
    // Store badge names but don't show popup yet - wait for level-up to close if it exists
    const storedBadges = sessionStorage.getItem('quizNewBadges');
    if (storedBadges) {
      try {
        const badgeNames = JSON.parse(storedBadges);
        if (badgeNames && badgeNames.length > 0) {
          setNewBadges(badgeNames);
          // If no level-up popup, show badge popup immediately
          // Otherwise, it will be shown after level-up closes
          const storedLevelUp = sessionStorage.getItem('quizLevelUp');
          if (!storedLevelUp) {
            setShowBadgePopup(true);
          }
          // Clear from sessionStorage after reading
          sessionStorage.removeItem('quizNewBadges');
        }
      } catch (err) {
        console.error('Failed to parse badge data:', err);
        sessionStorage.removeItem('quizNewBadges');
      }
    }
  }, []);

  // Show badge popup after level-up closes if badges were earned
  useEffect(() => {
    if (!showLevelUp && newBadges.length > 0 && !showBadgePopup) {
      setShowBadgePopup(true);
    }
  }, [showLevelUp, newBadges.length, showBadgePopup]);

  const formatTime = (milliseconds: number) => {
    const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const shareHomeUrl = typeof window !== 'undefined' ? `${window.location.origin}/` : 'https://cognidaily.com/';
  const topicLabel = share?.topicName ? ` ${share.topicName} quiz` : ' daily quiz';
  const shareCopyText = share
    ? `I scored ${share.score.toLocaleString()} in today's${topicLabel}. Think you can beat that? ${shareHomeUrl}`
    : '';
  const shareCopyUrl = share ? shareHomeUrl : '';

  const handleCopyText = async () => {
    if (!shareCopyText) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareCopyText);
      } else {
        const helper = document.createElement('textarea');
        helper.value = shareCopyText;
        helper.style.position = 'fixed';
        helper.style.opacity = '0';
        document.body.appendChild(helper);
        helper.focus();
        helper.select();
        document.execCommand('copy');
        document.body.removeChild(helper);
      }
      setCopyState('copied');
    } catch (copyError) {
      console.error('Copy failed', copyError);
      setCopyState('error');
    } finally {
      setTimeout(() => setCopyState('idle'), 3000);
    }
  };

  const handleCopyLink = async () => {
    if (!shareCopyUrl) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareCopyUrl);
        setShareLinkCopied(true);
      } else {
        const helper = document.createElement('textarea');
        helper.value = shareCopyUrl;
        helper.style.position = 'fixed';
        helper.style.opacity = '0';
        document.body.appendChild(helper);
        helper.focus();
        helper.select();
        document.execCommand('copy');
        document.body.removeChild(helper);
        setShareLinkCopied(true);
      }
    } catch (copyError) {
      console.error('Copy failed', copyError);
    } finally {
      setTimeout(() => setShareLinkCopied(false), 3000);
    }
  };

  // Build social share URLs
  const buildSocialUrls = (): SocialPlatform[] => {
    if (!share) return [];

    const encodedText = encodeURIComponent(shareCopyText);
    const encodedUrl = encodeURIComponent(shareCopyUrl);
    const encodedTitle = encodeURIComponent(`${share.username}'s Quiz Results`);

    return [
      {
        id: 'x',
        name: 'X (Twitter)',
        icon: '𝕏',
        url: `https://twitter.com/intent/tweet?text=${encodedText}`,
        color: '#000000'
      },
      {
        id: 'facebook',
        name: 'Facebook',
        icon: 'f',
        url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
        color: '#1877F2'
      },
      {
        id: 'linkedin',
        name: 'LinkedIn',
        icon: 'in',
        url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
        color: '#0A66C2'
      },
      {
        id: 'reddit',
        name: 'Reddit',
        icon: 'r',
        url: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
        color: '#FF4500'
      },
      {
        id: 'whatsapp',
        name: 'WhatsApp',
        icon: 'wa',
        url: `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}`,
        color: '#25D366'
      },
      {
        id: 'telegram',
        name: 'Telegram',
        icon: 'tg',
        url: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
        color: '#0088cc'
      },
      {
        id: 'email',
        name: 'Email',
        icon: '✉',
        url: `mailto:?subject=${encodeURIComponent('Check out my Cognidaily results')}&body=${encodedText}%0A%0A${encodedUrl}`,
        color: '#333333'
      }
    ];
  };

  if (loading) {
    return <main><p>Loading your results...</p></main>;
  }

  if (error || !share) {
    return (
      <main className="share-page">
        <h1>Share link unavailable</h1>
        <p>{error || 'We could not load this run. The link may have expired or been removed.'}</p>
        <div className="share-page__actions">
          <Link to="/" className="btn btn-primary">Go home</Link>
          <Link to="/register" className="btn btn-secondary">Join the competition</Link>
        </div>
      </main>
    );
  }

  const performanceHeadline = getPerformanceHeadline(share);
  const percentileText = formatPercentile(share.percentile, share.totalPlayersToday);
  const socialPlatforms = buildSocialUrls();

  return (
    <>
      <ShareMetaTags
        username={share.username}
        score={share.score}
        rank={share.rank}
        shareUrl={shareCopyUrl}
        percentile={share.percentile}
      />
      {showLevelUp && levelUpData && (
        <ResultModal
          isOpen={true}
          onClose={() => setShowLevelUp(false)}
          title="Level Up!"
          actionLabel="Continue"
        >
          <p>You&apos;ve reached Level {levelUpData.newLevel}!</p>
          <p>+{levelUpData.xpEarned.toLocaleString()} XP — Total: {levelUpData.totalXP.toLocaleString()} XP</p>
        </ResultModal>
      )}

      {/* Badge Achievement Popup */}
      {showBadgePopup && newBadges.length > 0 && (
        <BadgeAchievementPopup
          badgeNames={newBadges}
          onClose={() => {
            setShowBadgePopup(false);
            setNewBadges([]);
          }}
        />
      )}
      <main className="share-page">
        <header className="share-page__header">
          <p className="share-page__meta">
            {new Date(share.dateCompleted).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            {share.topicName ? ` · ${share.topicName}` : ''}
            {(userCountry?.country || user?.country || share.country || share.countryCode) && (
              <>
                {' · '}
                <CountryBadge
                  countryCode={userCountry?.countryCode || share.countryCode || (share.country && share.country.length === 2 ? share.country : undefined)}
                  countryName={userCountry?.country || user?.country || (share.country && share.country.length > 2 ? share.country : undefined)}
                  fallback={userCountry?.country || user?.country || share.country || share.countryCode}
                />
              </>
            )}
          </p>
          {share.isTopInCountry && <span className="share-page__badge">🏆 Country Champion</span>}
          {share.rank === 1 && <span className="share-page__badge">👑 #1 Today</span>}
          {percentileText && share.percentile && share.percentile >= 95 && (
            <span className="share-page__badge">⭐ {percentileText}</span>
          )}
        </header>

        <div className="share-page__body">
          <div className="share-page__main">
            <section className="share-page__hero">
              <h1 className="share-page__username">{share.username}{share.isPremium ? ' ★' : ''}</h1>
              <p className="share-page__headline">{performanceHeadline}</p>
              <p className="share-page__score">{displayScore.toLocaleString()} pts</p>
              {xpData && (
                <p className="share-page__xp">
                  +{xpData.xpEarned.toLocaleString()} XP · Level {Math.max(1, xpData.currentLevel)}
                </p>
              )}
            </section>

            <dl className="share-page__stats">
              <div className="share-page__stat">
                <dt>Rank</dt>
                <dd>#{displayRank}</dd>
              </div>
              <div className="share-page__stat">
                <dt>Accuracy</dt>
                <dd>{displayAccuracy}%</dd>
              </div>
              <div className="share-page__stat">
                <dt>Time</dt>
                <dd>{formatTime(share.completionTime)}</dd>
              </div>
              {percentileText && (
                <div className="share-page__stat">
                  <dt>Global</dt>
                  <dd>{percentileText}</dd>
                </div>
              )}
              {share.countryRank != null && share.countryTotalPlayers != null && (
                <div className="share-page__stat">
                  <dt>Country</dt>
                  <dd>#{share.countryRank}</dd>
                </div>
              )}
              {share.userStreak != null && share.userStreak > 0 && (
                <div className="share-page__stat">
                  <dt>Streak</dt>
                  <dd>{share.userStreak} days</dd>
                </div>
              )}
            </dl>

            <div className="share-page__actions">
              <Link to="/leaderboard" className="btn btn-primary btn-full btn--mobile-full">
                Leaderboard
              </Link>
              <Link to="/compete" className="btn btn-secondary btn-full btn--mobile-full">
                Compete
              </Link>
            </div>
          </div>

          <aside className="share-page__aside">
            <section className="share-page__share">
              <h2 className="share-page__share-title">Share</h2>
              <div className="share-page__copy-group">
                <button
                  type="button"
                  className="btn btn-primary btn-full"
                  onClick={handleCopyText}
                >
                  {copyState === 'copied' ? '✓ Copied!' : 'Copy to clipboard'}
                </button>
              </div>
              <details className="share-page__details">
                <summary className="share-page__details-summary">Edit before sharing</summary>
                <textarea
                  className="share-page__textarea"
                  value={shareCopyText}
                  readOnly
                  rows={3}
                  aria-label="Share text"
                />
              </details>
              <div className="share-page__copy-group">
                <input
                  type="text"
                  className="share-page__input"
                  value={shareCopyUrl}
                  readOnly
                  aria-label="Share link"
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-full"
                  onClick={handleCopyLink}
                >
                  {shareLinkCopied ? '✓ Copied!' : 'Copy link'}
                </button>
              </div>
              {copyState === 'error' && (
                <p className="share-page__copy-error">Copy failed. Try manually.</p>
              )}
              <p className="share-page__social-label">Share on:</p>
              <div className="share-page__social-links">
                {socialPlatforms.map((p) => (
                  <a
                    key={p.id}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-page__social-link"
                    title={p.name}
                  >
                    {p.name}
                  </a>
                ))}
              </div>
            </section>
          </aside>
        </div>

        {share?.breakdown && share.breakdown.length > 0 && (
          <QuizBreakdown
            questions={share.breakdown}
            title="Quiz Breakdown"
            className="share-page__breakdown"
          />
        )}
      </main>
    </>
  );
};

export default Share;
